/* eslint-disable @typescript-eslint/no-unused-vars */
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React, { CSSProperties, InputHTMLAttributes, ReactNode } from 'react';
import { breakpoints, colors, spacing } from '../../../../utils/styleguide';
import { colorsKurppa } from '../../../../utils/styleguideKurppa';
import Icon, { IconNames } from '../../Icon';
import Typography, { variants } from '../../text/Typography';
import { Variant } from '../../types/VariantTypes';
import { LabelContainer } from './LabelContainer';

type IconPropsType = {
  name: IconNames;
  fill?: string;
};

const iconProps: { [key: string]: IconPropsType } = {
  disabled: {
    name: 'lock',
  },
  error: {
    name: 'x-filled',
    fill: colors.red,
  },
  success: {
    name: 'check-filled',
    fill: colors.green,
  },
};

const getIcon = (hasError?: boolean, hasSuccess?: boolean, disabled?: boolean) => {
  let variantToRender: 'error' | 'success' | 'disabled' | undefined;
  if (disabled) {
    variantToRender = 'disabled';
  } else if (hasError) {
    variantToRender = 'error';
  } else if (hasSuccess) {
    variantToRender = 'success';
  }
  return (
    variantToRender && (
      <Icon
        css={css`
          position: absolute;
          right: 16px;
          top: 20px;
          pointer-events: none;
        `}
        {...iconProps[variantToRender]}
        size="16"
      />
    )
  );
};

type BaseTextProps = {
  value?: string;
  multiline?: boolean;
  hasFocus?: boolean;
  hasError?: boolean;
  hasSuccess?: boolean;
  straightRightBorder?: boolean;
  skipLeftBorder?: boolean;
  maxLength?: number;
  variant?: Variant;
} & InputHTMLAttributes<HTMLElement>;

const BaseText = ({
  value,
  multiline,
  hasFocus,
  hasError,
  hasSuccess,
  straightRightBorder,
  skipLeftBorder,
  ...rest
}: BaseTextProps) => {
  if (multiline) {
    return (
      <div
        className={rest.className}
        css={css`
          padding-right: 0 !important;
          padding-left: 0 !important;
          padding-bottom: 0 !important;
        `}
      >
        <textarea
          css={css`
            padding-top: 0 !important;
            border: none !important;
            resize: vertical;
          `}
          rows={value ? value.split('\n').length : 1}
          {...rest}
          value={value}
        />
      </div>
    );
  }
  return <input {...rest} value={value} />;
};

const Input = styled(BaseText)`
  ${variants.inputField.css}
  ${({ disabled, variant }) => {
    let color = variant === 'light' ? colors.black : colorsKurppa.grey02;
    if (disabled) {
      color = variant === 'light' ? colors.grey : colors.darkGray;
    }
    return `color: ${color};`;
  }}
  outline: none;
  margin: 0;
  width: 100%;
  border: ${({ variant }) => {
    if (variant === 'kurppa-dark') return 'none';
    return variant === 'light' ? `1px solid ${colors.grey}` : `1px solid ${colorsKurppa.green200}`;
  }};
  border-left-width: ${({ skipLeftBorder }) => (skipLeftBorder ? 0 : 1)};
  transition: border-color 200ms cubic-bezier(0, 0, 0.2, 1) 0ms;
  background-color: ${({ variant, disabled }) =>
    variant === 'light'
      ? disabled
        ? colors.lightGray
        : colors.white
      : `rgba(255, 255, 255, 0.1);`};
  padding: ${spacing[4]}px ${spacing[3]}px ${spacing[1]}px;
  height: ${({ multiline }) => (multiline ? 'auto' : '24px')};
  min-height: ${({ multiline }) => (multiline ? '96px' : '24px')};
  position: relative;
  display: flex;
  align-items: flex-end;
  min-width: 0;
  color-scheme: ${({ variant }) => {
    return variant === 'light' ? 'light' : 'dark';
  }};
  ${(props) =>
    props.type === 'password' &&
    css`
      font-family: caption;
      letter-spacing: 1px;
    `}

  ${({ hasFocus, hasError, hasSuccess, disabled, skipLeftBorder, variant }) => {
    let borderColor = variant === 'light' ? colors.black : colors.whiteTransparentSemi;
    const borderWidth = getBorderWidth({ hasFocus, hasError, hasSuccess });
    let paddingOffset = 1;

    if (hasError) borderColor = variant === 'light' ? colors.red : colors.error;
    else if (hasSuccess) {
      borderColor = colors.success;
    } else if (disabled) {
      borderColor = variant === 'light' ? colors.gray : colors.whiteTransparentSemi;
      paddingOffset = 0;
    }

    return (
      (hasFocus || hasError || hasSuccess || disabled) &&
      css`
        border-color: ${borderColor};
        border-width: ${borderWidth};
        padding: ${spacing[4] - paddingOffset}px ${spacing[3] - paddingOffset}px
          ${spacing[1] - paddingOffset}px;
        border-left-width: ${skipLeftBorder ? 0 : borderWidth};
      `
    );
  }}
`;

const getBorderWidth = ({
  hasFocus,
  hasError,
  hasSuccess,
}: {
  hasFocus?: boolean;
  hasError?: boolean;
  hasSuccess?: boolean;
}) => (hasFocus || hasError || hasSuccess ? '2px' : '1px');

type InfoBoxProps = {
  infoBoxBackground: string;
};
const InfoBox = styled.div`
  padding: ${spacing[1]}px ${spacing[2]}px;
  background-color: ${({ infoBoxBackground }: InfoBoxProps) => infoBoxBackground};
  margin-top: ${spacing[0]}px;
`;

const getColors = ({
  variant,
  disabled,
  showSuccess,
  successMessage,
  labelLifted,
  error,
}: CommonProps) => {
  const labelColor = variant === 'light' ? colors.darkGrey : colors.gray;
  const infoTextColor = colors.darkGrey;
  const infoBoxBackground = 'transparent';

  if (error) {
    const errorColor = variant === 'light' ? colors.red : colors.error;
    return {
      labelColor: errorColor,
      infoTextColor: errorColor,
      infoBoxBackground: variant === 'light' ? colors.lightRed : infoBoxBackground,
    };
  }

  if (!!successMessage || showSuccess) {
    return {
      labelColor: colors.success,
      infoTextColor: variant === 'light' ? colors.greenDark : colors.success,
      infoBoxBackground: variant === 'light' ? colors.lightGreen : infoBoxBackground,
    };
  }

  if (disabled) {
    return {
      labelColor: variant === 'dark' ? colors.darkGray : colors.grey,
      infoTextColor,
      infoBoxBackground,
    };
  }

  if (labelLifted && variant === 'light') {
    return {
      infoTextColor,
      labelColor: colors.black,
      infoBoxBackground,
    };
  }

  return {
    infoTextColor,
    labelColor,
    infoBoxBackground,
  };
};

type CommonProps = {
  disabled?: boolean;
  error?: string | ReactNode;
  successMessage?: string;
  showSuccess?: boolean;
  labelLifted?: boolean;
  variant?: Variant;
};

type InputBaseProps = CommonProps & {
  className?: string;
  instructions?: string;
  label?: string;
  type?: string;
  hasFocus?: boolean;
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onInvalid?: (event: React.InvalidEvent<HTMLInputElement>) => void;
  multiline?: boolean;
  name?: string;
  required?: boolean;
  straightRightBorder?: boolean;
  skipLeftBorder?: boolean;
  maxLength?: number;
  autoComplete?: string;
  autoFocus?: boolean;
  placeholder?: string;
  style?: CSSProperties;
};

const InputBase = ({
  className,
  disabled,
  error,
  successMessage,
  showSuccess,
  instructions,
  hasFocus,
  labelLifted,
  label,
  type,
  value,
  onChange,
  onFocus,
  onBlur,
  onInvalid,
  multiline,
  name,
  required,
  straightRightBorder,
  skipLeftBorder,
  maxLength,
  autoComplete,
  autoFocus,
  placeholder,
  style,
  variant = 'light',
}: InputBaseProps) => {
  const { labelColor, infoTextColor, infoBoxBackground } = getColors({
    variant,
    disabled,
    showSuccess,
    successMessage,
    labelLifted,
    error,
  });

  return (
    <div
      className={className}
      css={css`
        margin-bottom: ${spacing[1]}px;
        ${breakpoints.tablet} {
          margin-bottom: ${spacing[2]}px;
        }
      `}
    >
      <label
        css={css`
          position: relative;
          display: flex;
        `}
      >
        <LabelContainer labelColor={labelColor} labelLifted={labelLifted} variant={variant}>
          {label}
        </LabelContainer>
        <Input
          type={type}
          hasFocus={hasFocus}
          value={value}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          hasError={!!error}
          hasSuccess={!!successMessage || showSuccess}
          disabled={disabled}
          multiline={multiline}
          name={name}
          required={required}
          straightRightBorder={straightRightBorder}
          skipLeftBorder={skipLeftBorder}
          maxLength={maxLength}
          onInvalid={onInvalid}
          {...(autoComplete && { autoComplete: autoComplete })}
          autoFocus={autoFocus}
          placeholder={placeholder}
          style={style}
          variant={variant}
        />
        {getIcon(!!error, !!successMessage || showSuccess, disabled)}
      </label>
      {(error || successMessage || instructions) && (
        <InfoBox infoBoxBackground={infoBoxBackground}>
          <Typography useBuiltInMargins variant="detail" tag="div" color={infoTextColor}>
            {error ? error : successMessage || instructions}
          </Typography>
        </InfoBox>
      )}
      {maxLength && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Typography variant="detail" color={colors.darkGray}>{`${
            value?.length || 0
          }/${maxLength}`}</Typography>
        </div>
      )}
    </div>
  );
};

export default InputBase;
